'use client'

import * as React from 'react'
import { ParsedTab } from '../explore/components/tabs/store'
import { Button } from '~/sotano-kit/primitives/button'
import {
  AvailableModel,
  AvailableModelWithHide,
  FREE_MODELS,
  PREMIUM_MODELS,
  Repository,
  mergeWithFreeModels,
} from '@basehub/data/replicache/schema/repository'
import { BreadcrumbStickyContainer } from '../explore/components/tabs/dashboard-breadcrumb'
import { Breadcrumb, BreadcrumbItem } from '~/sotano-kit/primitives/breadcrumb'
import { TabContentDefaultWrapper } from '../tab-content/wrapper'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { isUserRepo } from '@basehub/data/replicache/mutators/_public-types'
import { useTeamReplicache } from '~/hooks-data/use-team-replicache'
import { ExternalLinkIcon } from '@radix-ui/react-icons'
import { Checkbox } from '~/sotano-kit/primitives/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '~/sotano-kit/primitives/dialog'
import { useQuery } from '@tanstack/react-query'
import { ScrollArea } from '~/sotano-kit/primitives/scroll-area'

// OpenRouter API types
interface OpenRouterModel {
  id: string
  name: string
}

interface OpenRouterModelsResponse {
  models: OpenRouterModel[]
}

// Unified Premium Model Card Component
interface PremiumModelCardProps {
  model: {
    openrouterId: string
    label: string
  }
  isAdded: boolean
  onToggle: (modelId: string) => void
  disabled?: boolean
}

const PremiumModelCard = ({
  model,
  isAdded,
  onToggle,
  disabled = false,
}: PremiumModelCardProps) => {
  return (
    <div className="flex items-center justify-between p-3 border border-dark-border-solid rounded-lg bg-dark-bg">
      <div className="flex items-center gap-3">
        <div className="flex flex-col gap-2">
          <div className="font-medium text-sm leading-none text-dark-bright">
            {model.label}
          </div>
          <div className="flex">
            <a
              href={`https://openrouter.ai/${model.openrouterId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs leading-none text-dark-muted font-mono hover:underline group inline-flex items-center gap-1"
            >
              {model.openrouterId}
              <ExternalLinkIcon className="w-2.5 h-2.5 opacity-0 group-hover:opacity-100" />
            </a>
          </div>
        </div>
      </div>
      <Button
        intent={isAdded ? 'secondary' : 'primary'}
        size="small"
        onClick={() => !disabled && onToggle(model.openrouterId)}
        disabled={disabled}
      >
        {disabled && isAdded ? 'Added' : isAdded ? 'Remove' : 'Add'}
      </Button>
    </div>
  )
}

// PKCE helper functions based on OpenRouter documentation
async function generateCodeVerifier(): Promise<string> {
  const array = new Uint8Array(32)
  crypto.getRandomValues(array)
  return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join(
    ''
  )
}

async function generateCodeChallenge(codeVerifier: string): Promise<string> {
  const encoder = new TextEncoder()
  const data = encoder.encode(codeVerifier)
  const hash = await crypto.subtle.digest('SHA-256', data)
  // Convert to base64url encoding
  return btoa(String.fromCharCode(...new Uint8Array(hash)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '')
}

async function exchangeCodeForApiKey(
  code: string,
  codeVerifier: string
): Promise<string> {
  const response = await fetch('https://openrouter.ai/api/v1/auth/keys', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      code,
      code_verifier: codeVerifier,
      code_challenge_method: 'S256',
    }),
  })

  if (!response.ok) {
    throw new Error(`OpenRouter auth failed: ${response.statusText}`)
  }

  const result = (await response.json()) as { key: string }
  return result.key
}

interface ModelsListProps {
  models: AvailableModel[]
  minOneRequired?: boolean
  repository: Repository
  teamReplicache:
    | NonNullable<ReturnType<typeof useTeamReplicache>['data']>['replicache']
    | undefined
}

const ModelsList = ({
  models,
  minOneRequired = false,
  repository,
  teamReplicache,
}: ModelsListProps) => {
  // Get the merged models from props (calculated in parent component)
  const mergedModels = React.useMemo(() => {
    const currentAvailableModels =
      repository.agentsConfig?.availableModels || []
    const hasOpenRouterKey = Boolean(repository.agentsConfig?.openRouterApiKey)
    return mergeWithFreeModels(currentAvailableModels, hasOpenRouterKey)
  }, [
    repository.agentsConfig?.availableModels,
    repository.agentsConfig?.openRouterApiKey,
  ])

  const selectedModelIds = new Set(mergedModels.map((m) => m.openrouterId))

  // Filter models to show: either not hidden, or hidden but explicitly selected
  const visibleModels = models.filter((model) => {
    const freeModel = (FREE_MODELS as AvailableModelWithHide[]).find(
      (fm) => fm.openrouterId === model.openrouterId
    )
    if (!freeModel) return true // Non-free models are always visible

    // Free model: show if not hidden, or if hidden but explicitly selected
    return !freeModel.hide || selectedModelIds.has(model.openrouterId)
  })

  const handleToggleModel = async (modelId: string) => {
    if (!teamReplicache) return

    // Work with the raw stored selection, not the merged one
    const currentStoredModels = repository.agentsConfig?.availableModels || []
    const isCurrentlySelected = selectedModelIds.has(modelId)
    const hasOpenRouterKey = Boolean(repository.agentsConfig?.openRouterApiKey)

    const freeModelsOnly = (FREE_MODELS as AvailableModelWithHide[]).filter(
      (model) => !model.hide
    )
    const premiumModelsOnly = hasOpenRouterKey ? PREMIUM_MODELS : []
    const defaultModels = [...freeModelsOnly, ...premiumModelsOnly]

    // Check how many default models are currently stored (not merged)
    const storedDefaultModels = currentStoredModels.filter((model) =>
      defaultModels.some(
        (defaultModel) => defaultModel.openrouterId === model.openrouterId
      )
    )

    let newStoredModels: AvailableModel[]

    if (isCurrentlySelected) {
      // Unchecking a model
      if (defaultModels.some((dm) => dm.openrouterId === modelId)) {
        // Unchecking a default model (free or premium)
        if (storedDefaultModels.length === 0) {
          // No default models are stored (we're showing all via mergeWithFreeModels)
          // User unchecks their first default model -> store all OTHER default models
          const allOtherDefaultModels = defaultModels.filter(
            (dm) => dm.openrouterId !== modelId
          )
          const nonDefaultModels = currentStoredModels.filter(
            (model) =>
              !defaultModels.some(
                (dm) => dm.openrouterId === model.openrouterId
              )
          )
          newStoredModels = [...nonDefaultModels, ...allOtherDefaultModels]
        } else {
          // Some default models are already stored, just remove this one
          // (Note: The UI should prevent unchecking the last free model via disabled state)
          newStoredModels = currentStoredModels.filter(
            (model) => model.openrouterId !== modelId
          )
        }
      } else {
        // Unchecking a non-default model (manually added)
        newStoredModels = currentStoredModels.filter(
          (model) => model.openrouterId !== modelId
        )
      }
    } else {
      // Checking a model - add it to stored models
      const modelToAdd = models.find((m) => m.openrouterId === modelId)
      if (modelToAdd) {
        const potentialNewModels = [...currentStoredModels, modelToAdd]

        // If this would result in ALL default models being stored, store NONE instead
        // (mergeWithFreeModels will show them all at runtime)
        if (defaultModels.some((dm) => dm.openrouterId === modelId)) {
          const potentialStoredDefaultModels = potentialNewModels.filter(
            (model) =>
              defaultModels.some(
                (defaultModel) =>
                  defaultModel.openrouterId === model.openrouterId
              )
          )

          if (potentialStoredDefaultModels.length === defaultModels.length) {
            // All default models would be stored - remove them all instead
            newStoredModels = potentialNewModels.filter(
              (model) =>
                !defaultModels.some(
                  (dm) => dm.openrouterId === model.openrouterId
                )
            )
          } else {
            newStoredModels = potentialNewModels
          }
        } else {
          // Non-default model, just add it
          newStoredModels = potentialNewModels
        }
      } else {
        return
      }
    }

    // Store the raw selection without merging logic
    try {
      await teamReplicache.mutate.updateRepositoryAgentsConfig({
        repoId: repository.id,
        config: {
          openRouterApiKey: repository.agentsConfig?.openRouterApiKey || null,
          availableModels: newStoredModels,
        },
      })
    } catch (error) {
      console.error('Failed to update model selection:', error)
    }
  }

  if (visibleModels.length === 0) {
    return (
      <div className="border border-dark-border rounded-lg p-4">
        <div className="text-center py-4 text-dark-muted text-sm">
          No models available
        </div>
      </div>
    )
  }

  return (
    <div className="border border-dark-border-solid rounded-lg divide-y divide-dark-border-solid">
      {visibleModels.map((model, index) => {
        const isSelected = selectedModelIds.has(model.openrouterId)
        const _isFirstFreeModel = FREE_MODELS.some(
          (fm) => fm.openrouterId === model.openrouterId
        )

        // For free models, require at least one to be selected
        // Get the actual stored default models count for disabling logic
        const currentStoredModels =
          repository.agentsConfig?.availableModels || []
        const hasOpenRouterKey = Boolean(
          repository.agentsConfig?.openRouterApiKey
        )
        const freeModelsOnly = (FREE_MODELS as AvailableModelWithHide[]).filter(
          (model) => !model.hide
        )
        const premiumModelsOnly = hasOpenRouterKey ? PREMIUM_MODELS : []
        const defaultModels = [...freeModelsOnly, ...premiumModelsOnly]
        const storedDefaultModels = currentStoredModels.filter((model) =>
          defaultModels.some(
            (defaultModel) => defaultModel.openrouterId === model.openrouterId
          )
        )

        // Disable if:
        // - minOneRequired is true
        // - This is a free model (premium models can always be unchecked)
        // - It's currently selected
        // - It would be the last stored free model among all default models
        const isFreeModel = freeModelsOnly.some(
          (fm) => fm.openrouterId === model.openrouterId
        )
        const storedFreeModels = storedDefaultModels.filter((model) =>
          freeModelsOnly.some((fm) => fm.openrouterId === model.openrouterId)
        )
        const disabled =
          minOneRequired &&
          isFreeModel &&
          isSelected &&
          storedFreeModels.length === 1 &&
          storedFreeModels[0]?.openrouterId === model.openrouterId

        const isFirst = index === 0
        const isLast = index === visibleModels.length - 1

        return (
          <div
            key={model.openrouterId}
            className={`
              p-3 bg-dark-bg
              ${disabled ? 'cursor-default' : 'hover:bg-dark-shade'}
              ${isFirst ? 'rounded-t-lg' : ''}
              ${isLast ? 'rounded-b-lg' : ''}
            `}
            onClick={() => !disabled && handleToggleModel(model.openrouterId)}
          >
            <div className="flex items-center justify-between gap-4">
              <div className="flex-1 min-w-0 gap-2 flex flex-col">
                <div className="flex">
                  <span className="font-medium leading-none text-xs text-dark-bright inline-flex items-center gap-1">
                    {model.label}
                  </span>
                </div>
                <div className="flex">
                  <a
                    href={`https://openrouter.ai/${model.openrouterId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="text-xs leading-none text-dark-muted font-mono hover:underline group inline-flex items-center gap-1"
                  >
                    {model.openrouterId}
                    <ExternalLinkIcon className="w-2.5 h-2.5 opacity-0 group-hover:opacity-100" />
                  </a>
                </div>
              </div>

              <div className="flex-shrink-0 flex items-center">
                <Checkbox
                  checked={isSelected}
                  disabled={disabled}
                  onCheckedChange={(checked) => {
                    if (!disabled && typeof checked === 'boolean') {
                      handleToggleModel(model.openrouterId)
                    }
                  }}
                  className="pointer-events-none" // Since the parent div handles clicks
                  clickTargetPadding={4}
                />
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

// Model Search Dialog Component
interface ModelSearchDialogProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  repository: Repository
  onAddModel: (model: OpenRouterModel) => void
  onRemoveModel: (modelId: string) => void
}

const ModelSearchDialog = ({
  isOpen,
  onOpenChange,
  repository,
  onAddModel,
  onRemoveModel,
}: ModelSearchDialogProps) => {
  const [searchQuery, setSearchQuery] = React.useState('')

  // Fetch all OpenRouter models using useQuery
  const {
    data: allModels = [],
    isLoading: isLoadingModels,
    error: _modelsError,
  } = useQuery<OpenRouterModel[]>({
    queryKey: ['openrouter-models'],
    queryFn: async () => {
      const response = await fetch('/api/ad-hoc/or-models')
      if (!response.ok) throw new Error('Failed to fetch models')
      const data = (await response.json()) as OpenRouterModelsResponse
      return data.models
    },
    staleTime: Infinity,
    enabled: isOpen, // Only fetch when dialog is open
  })

  // Reset search when dialog closes
  React.useEffect(() => {
    if (!isOpen) {
      setSearchQuery('')
    }
  }, [isOpen])

  // Filter models based on search query
  const filteredModels = allModels.filter(
    (model) =>
      model.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      model.id.toLowerCase().includes(searchQuery.toLowerCase())
  )

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="!max-w-2xl w-full max-h-[80vh] flex flex-col !p-0">
        <DialogHeader className="!pt-4 !px-4">
          <DialogTitle>Add Model</DialogTitle>
          <DialogDescription>
            Search and add models from{' '}
            <a
              href="https://openrouter.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="text-dark-accent hover:underline"
            >
              OpenRouter
            </a>{' '}
            to your available models.
          </DialogDescription>
        </DialogHeader>

        {/* Search Input */}
        <div className="!px-4">
          <input
            type="text"
            placeholder="Search models by name or ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-3 py-2 border text-sm border-dark-border-solid rounded-lg bg-dark-bg text-dark-bright placeholder-dark-muted focus:outline-none focus:ring-2 focus:ring-dark-accent"
            autoFocus
          />
        </div>

        <div className="mt-4 h-px w-full bg-dark-border-solid" />

        {/* Model Results */}
        <ScrollArea className="h-[500px] !px-4">
          {isLoadingModels ? (
            <div className="text-center py-8 text-dark-muted">
              Loading models...
            </div>
          ) : filteredModels.length === 0 ? (
            <div className="text-center py-8 text-dark-muted">
              {searchQuery
                ? 'No models found matching your search.'
                : 'No models available.'}
            </div>
          ) : (
            <div className="pt-4 space-y-2">
              {filteredModels.slice(0, 25).map((model) => {
                const isAlreadyAdded = Boolean(
                  repository.agentsConfig?.availableModels?.some(
                    (availableModel) => availableModel.openrouterId === model.id
                  )
                )

                return (
                  <PremiumModelCard
                    key={model.id}
                    model={{
                      openrouterId: model.id,
                      label: model.name,
                    }}
                    isAdded={isAlreadyAdded}
                    onToggle={() => {
                      if (isAlreadyAdded) {
                        // Remove the model
                        onRemoveModel(model.id)
                      } else {
                        // Add the model
                        onAddModel(model)
                      }
                    }}
                    disabled={false}
                  />
                )
              })}
            </div>
          )}

          {filteredModels.length > 25 && (
            <div className="text-center py-2 text-dark-muted text-xs">
              Showing first 25 results. Search to narrow down.
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  )
}

export const AgentsTabContent = ({
  parsedTab,
}: {
  parsedTab: ParsedTab & { type: 'agents' }
}) => {
  const teamReplicache = useTeamReplicache().data?.replicache
  const { data } = useRepoReplicache()
  const repository = data?.repo

  const [isConnecting, setIsConnecting] = React.useState(false)
  const [connectionError, setConnectionError] = React.useState<string | null>(
    null
  )
  const [showModelSearch, setShowModelSearch] = React.useState(false)

  // Get the current selection merged with free and premium models logic
  const mergedModels = React.useMemo(() => {
    if (!repository || !isUserRepo(repository)) return []
    const currentAvailableModels =
      repository.agentsConfig?.availableModels || []
    const hasOpenRouterKey = Boolean(repository.agentsConfig?.openRouterApiKey)
    return mergeWithFreeModels(currentAvailableModels, hasOpenRouterKey)
  }, [repository])

  const isOpenRouterConnected = Boolean(
    (repository as Repository)?.agentsConfig?.openRouterApiKey
  )

  // Handle OAuth callback on component mount
  React.useEffect(() => {
    const handleOAuthCallback = async () => {
      if (!repository || !isUserRepo(repository)) return

      const urlParams = new URLSearchParams(window.location.search)
      const code = urlParams.get('code')
      const storedCodeVerifier = localStorage.getItem(
        'openrouter_code_verifier'
      )

      if (code && storedCodeVerifier && teamReplicache) {
        setIsConnecting(true)
        setConnectionError(null)

        try {
          // Exchange code for API key
          const apiKey = await exchangeCodeForApiKey(code, storedCodeVerifier)

          // Save to repository
          await teamReplicache.mutate.updateRepositoryAgentsConfig({
            repoId: repository.id,
            config: {
              openRouterApiKey: apiKey,
              availableModels:
                (repository as Repository).agentsConfig?.availableModels || [],
            },
          })

          // Clear the code from URL and localStorage
          localStorage.removeItem('openrouter_code_verifier')
          const newUrl = new URL(window.location.href)
          newUrl.searchParams.delete('code')
          window.history.replaceState({}, '', newUrl.toString())
        } catch (error) {
          console.error('Failed to connect OpenRouter:', error)
          setConnectionError(
            error instanceof Error ? error.message : 'Failed to connect'
          )
        } finally {
          setIsConnecting(false)
        }
      }
    }

    handleOAuthCallback()
  }, [repository, teamReplicache])

  if (!isUserRepo(repository)) return null

  const handleConnectOpenRouter = async () => {
    try {
      // Generate PKCE parameters
      const codeVerifier = await generateCodeVerifier()
      const codeChallenge = await generateCodeChallenge(codeVerifier)

      // Store code verifier for later use
      localStorage.setItem('openrouter_code_verifier', codeVerifier)

      // Build OAuth URL
      const callbackUrl =
        window.location.origin +
        window.location.pathname +
        window.location.search
      const authUrl = new URL('https://openrouter.ai/auth')
      authUrl.searchParams.set('callback_url', callbackUrl)
      authUrl.searchParams.set('code_challenge', codeChallenge)
      authUrl.searchParams.set('code_challenge_method', 'S256')

      // Redirect to OpenRouter
      window.location.href = authUrl.toString()
    } catch (error) {
      console.error('Failed to initiate OpenRouter connection:', error)
      setConnectionError('Failed to initiate connection')
    }
  }

  const handleDisconnectOpenRouter = async () => {
    try {
      if (!teamReplicache) return

      await teamReplicache.mutate.updateRepositoryAgentsConfig({
        repoId: repository.id,
        config: {
          openRouterApiKey: null,
          availableModels:
            (repository as Repository).agentsConfig?.availableModels || [],
        },
      })
    } catch (error) {
      console.error('Failed to disconnect OpenRouter:', error)
      setConnectionError('Failed to disconnect')
    }
  }

  const handleAddModel = async (model: AvailableModel) => {
    try {
      if (!teamReplicache) return

      const currentModels =
        (repository as Repository).agentsConfig?.availableModels || []
      const isAlreadyAdded = currentModels.some(
        (existingModel) => existingModel.openrouterId === model.openrouterId
      )

      if (isAlreadyAdded) return

      const updatedModels = [...currentModels, model]

      await teamReplicache.mutate.updateRepositoryAgentsConfig({
        repoId: repository.id,
        config: {
          openRouterApiKey:
            (repository as Repository).agentsConfig?.openRouterApiKey || null,
          availableModels: updatedModels,
        },
      })
    } catch (error) {
      console.error('Failed to add model:', error)
      setConnectionError('Failed to add model')
    }
  }

  const handleAddModelFromSearch = async (openRouterModel: OpenRouterModel) => {
    const availableModel: AvailableModel = {
      openrouterId: openRouterModel.id,
      label: openRouterModel.name,
    }
    await handleAddModel(availableModel)
  }

  const openModelSearch = () => {
    setShowModelSearch(true)
  }

  const handleRemoveModel = async (modelId: string) => {
    try {
      if (!teamReplicache) return

      // Use the same logic as handleToggleModel for removing models
      const currentStoredModels = repository.agentsConfig?.availableModels || []
      const hasOpenRouterKey = Boolean(
        repository.agentsConfig?.openRouterApiKey
      )

      const freeModelsOnly = (FREE_MODELS as AvailableModelWithHide[]).filter(
        (model) => !model.hide
      )
      const premiumModelsOnly = hasOpenRouterKey ? PREMIUM_MODELS : []
      const defaultModels = [...freeModelsOnly, ...premiumModelsOnly]

      const storedDefaultModels = currentStoredModels.filter((model) =>
        defaultModels.some(
          (defaultModel) => defaultModel.openrouterId === model.openrouterId
        )
      )

      let newStoredModels: AvailableModel[]

      if (defaultModels.some((dm) => dm.openrouterId === modelId)) {
        // Removing a default model (free or premium)
        if (storedDefaultModels.length === 0) {
          // No default models are stored (we're showing all via mergeWithFreeModels)
          // User removes their first default model -> store all OTHER default models
          const allOtherDefaultModels = defaultModels.filter(
            (dm) => dm.openrouterId !== modelId
          )
          const nonDefaultModels = currentStoredModels.filter(
            (model) =>
              !defaultModels.some(
                (dm) => dm.openrouterId === model.openrouterId
              )
          )
          newStoredModels = [...nonDefaultModels, ...allOtherDefaultModels]
        } else {
          // Some default models are already stored, just remove this one
          newStoredModels = currentStoredModels.filter(
            (model) => model.openrouterId !== modelId
          )
        }
      } else {
        // Removing a non-default model (manually added)
        newStoredModels = currentStoredModels.filter(
          (model) => model.openrouterId !== modelId
        )
      }

      await teamReplicache.mutate.updateRepositoryAgentsConfig({
        repoId: repository.id,
        config: {
          openRouterApiKey:
            (repository as Repository).agentsConfig?.openRouterApiKey || null,
          availableModels: newStoredModels,
        },
      })
    } catch (error) {
      console.error('Failed to remove model:', error)
      setConnectionError('Failed to remove model')
    }
  }

  // Use the organized model constants
  const freeModels: AvailableModel[] = FREE_MODELS

  if (parsedTab.tab === 'settings') {
    return (
      <>
        <BreadcrumbStickyContainer>
          <Breadcrumb>
            <BreadcrumbItem active>{parsedTab.label}</BreadcrumbItem>
          </Breadcrumb>
        </BreadcrumbStickyContainer>
        <TabContentDefaultWrapper fillerChildren>
          <div className="space-y-12">
            {/* Page Header */}
            <div>
              <h1 className="text-2xl font-medium text-dark-bright">
                Settings for Agents
              </h1>
              <p className="text-dark-muted">
                Configure models and external services for your agents
              </p>
            </div>

            {/* Free Models Section */}
            <div className="space-y-4">
              <div>
                <h2 className="text-lg font-medium text-dark-bright">
                  Free models
                </h2>
                <p className="text-sm text-dark-muted">
                  Models which you can run at no cost (fair usage policy
                  applies).
                </p>
              </div>
              <ModelsList
                models={freeModels}
                minOneRequired
                repository={repository as Repository}
                teamReplicache={teamReplicache}
              />
            </div>

            {/* Any Model Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-medium text-dark-bright">
                    Premium models
                  </h2>
                  <p className="text-sm text-dark-muted">
                    Models provided and funded by your own{' '}
                    <a
                      href="https://openrouter.ai"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-dark-accent hover:underline"
                    >
                      OpenRouter
                    </a>{' '}
                    account.
                  </p>
                </div>
                {isOpenRouterConnected && (
                  <div className="flex items-center gap-2">
                    <Button
                      intent="secondary"
                      onClick={handleDisconnectOpenRouter}
                      disabled={isConnecting}
                    >
                      {isConnecting
                        ? 'Disconnecting...'
                        : 'Disconnect OpenRouter'}
                    </Button>
                    <Button intent="primary" onClick={openModelSearch}>
                      Add model
                    </Button>
                  </div>
                )}
              </div>

              {/* OpenRouter Connection Status */}
              {!isOpenRouterConnected && (
                <div className="border border-dark-border-solid bg-dark-bg rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <div className="font-medium text-sm">
                        {isConnecting ? 'Connecting...' : 'Not Connected'}
                      </div>
                      <div className="text-xs text-dark-muted">
                        {isConnecting ? (
                          'Connecting to OpenRouter...'
                        ) : connectionError ? (
                          <span className="text-red-500">
                            {connectionError}
                          </span>
                        ) : (
                          'Connect your OpenRouter account to unlock additional AI models.'
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        intent="primary"
                        onClick={handleConnectOpenRouter}
                        disabled={isConnecting}
                      >
                        {isConnecting ? 'Connecting...' : 'Connect OpenRouter'}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Available Models when connected */}
              {isOpenRouterConnected && (
                <div className="space-y-6">
                  {/* Models Management Section */}
                  <div className="space-y-4">
                    {/* Current Models List */}
                    <div className="space-y-2">
                      {mergedModels
                        .filter(
                          (model: AvailableModel) =>
                            !FREE_MODELS.some(
                              (fm: AvailableModel) =>
                                fm.openrouterId === model.openrouterId
                            )
                        )
                        .map((model: AvailableModel) => (
                          <PremiumModelCard
                            key={model.openrouterId}
                            model={model}
                            isAdded={true}
                            onToggle={handleRemoveModel}
                          />
                        ))}

                      {mergedModels.filter(
                        (model: AvailableModel) =>
                          !FREE_MODELS.some(
                            (fm: AvailableModel) =>
                              fm.openrouterId === model.openrouterId
                          )
                      ).length === 0 && (
                        <div className="text-center py-8 text-dark-muted">
                          <p>No models added yet.</p>
                          <p className="text-xs">
                            Click "Add Model" to get started.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </TabContentDefaultWrapper>

        {/* Model Search Dialog */}
        <ModelSearchDialog
          isOpen={showModelSearch}
          onOpenChange={setShowModelSearch}
          repository={repository as Repository}
          onAddModel={handleAddModelFromSearch}
          onRemoveModel={handleRemoveModel}
        />
      </>
    )
  }

  return (
    <TabContentDefaultWrapper fillerChildren>
      <div className="text-center py-8 text-dark-muted">
        <p>Unknown agents tab: {parsedTab.tab}</p>
      </div>
    </TabContentDefaultWrapper>
  )
}
